import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container,
  Typography,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import { get } from "src/utils/networkUtils";
import Iconify from "../components/iconify";

const STATUS_COLORS = {
  completed: "success",
  finalised: "success",
  scheduled: "default",
  upcoming: "default",
  live: "warning",
  postponed: "error",
  cancelled: "error",
};

function unwrapListResponse(response, key) {
  return (
    response?.data?.[key] ??
    response?.data?.data?.[key] ??
    response?.[key] ??
    response?.data ??
    []
  );
}

function getDefaultTournamentCalendarId(tournaments) {
  const worldCup = tournaments.find((t) =>
    `${t.competition_name ?? ""} ${t.short_code ?? ""}`
      .toLowerCase()
      .includes("world cup")
  );
  const active = tournaments.find((t) => t.status === "active");
  return (worldCup ?? active ?? tournaments[0])?.id ?? "";
}

function formatTournamentLabel(tournament) {
  return [
    tournament.competition_name,
    tournament.season_label,
    tournament.short_code,
  ]
    .filter(Boolean)
    .join(" - ");
}

function getContestantName(fixture, side) {
  const nested = fixture[`${side}Contestant`]?.name;
  const flat = fixture[`${side}ContestantName`];
  return nested ?? flat ?? "-";
}

export default function EcffFixturesPage() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [fixtures, setFixtures] = useState(null);
  const [error, setError] = useState(null);
  const [loadingTournaments, setLoadingTournaments] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  const handleCopyId = async (id) => {
    const value = String(id ?? "");
    if (!value || !navigator?.clipboard?.writeText) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(value);
      setTimeout(
        () => setCopiedId((current) => (current === value ? null : current)),
        1500
      );
    } catch {
      // Clipboard can be unavailable (e.g. insecure context); ignore.
    }
  };

  useEffect(() => {
    let isMounted = true;

    get("/admin/tournament-calendar?page=1&limit=100")
      .then((r) => {
        if (!isMounted) return;
        const items = unwrapListResponse(r, "tournamentCalendars");
        const tournamentList = Array.isArray(items) ? items : [];
        setTournaments(tournamentList);
        setSelectedTournamentId(getDefaultTournamentCalendarId(tournamentList));
      })
      .catch((err) => {
        if (!isMounted) return;
        setTournaments([]);
        setSelectedTournamentId("");
        setError(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to fetch tournament calendars"
        );
      })
      .finally(() => {
        if (isMounted) setLoadingTournaments(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (loadingTournaments) return undefined;

    if (!selectedTournamentId) {
      setFixtures([]);
      return;
    }

    let isMounted = true;
    setFixtures(null);
    setError(null);

    const params = new URLSearchParams({
      tournamentCalendarId: selectedTournamentId,
      limit: "100",
    });

    get(`/admin/matches?${params.toString()}`)
      .then((r) => {
        if (!isMounted) return;
        const items = r?.data?.matches ?? r?.matches ?? r?.data?.items ?? [];
        setFixtures(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setFixtures([]);
        setError(
          err?.response?.data?.message ??
            err?.message ??
            "Failed to fetch fixtures"
        );
      });

    return () => {
      isMounted = false;
    };
  }, [selectedTournamentId, loadingTournaments]);

  const selectedTournament = tournaments.find(
    (t) => t.id === selectedTournamentId
  );

  return (
    <>
      <Helmet>
        <title> Fixtures | EngageCraft Fantasy Football Admin </title>
      </Helmet>

      <Container maxWidth="xl">
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box>
            <Typography variant="h4">
              Fixtures{fixtures !== null && ` (${fixtures.length})`}
            </Typography>
            {selectedTournament && (
              <Typography variant="body2" color="text.secondary">
                {formatTournamentLabel(selectedTournament)}
              </Typography>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 320 } }}>
            <InputLabel id="fixtures-tournament-label">Tournament</InputLabel>
            <Select
              labelId="fixtures-tournament-label"
              label="Tournament"
              value={selectedTournamentId}
              onChange={(event) => setSelectedTournamentId(event.target.value)}
              disabled={loadingTournaments || tournaments.length === 0}
            >
              {tournaments.map((tournament) => (
                <MenuItem key={tournament.id} value={tournament.id}>
                  {formatTournamentLabel(tournament)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Backend returned an error: <strong>{error}</strong>.
          </Alert>
        )}

        {!loadingTournaments && tournaments.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No tournament calendars found. Import a tournament before viewing
            fixtures.
          </Alert>
        )}

        <Card>
          {fixtures === null ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Gameweek</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Home</TableCell>
                    <TableCell>Away</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Score</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fixtures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        No fixtures yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    fixtures.map((f) => (
                      <TableRow key={f.id} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace", fontSize: 11 }}
                            >
                              {String(f.id ?? "-").slice(0, 8)}
                            </Typography>
                            {f.id && (
                              <Tooltip
                                title={
                                  copiedId === String(f.id)
                                    ? "Copied!"
                                    : "Copy database ID"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyId(f.id)}
                                  sx={{ p: 0.25 }}
                                >
                                  <Iconify
                                    icon={
                                      copiedId === String(f.id)
                                        ? "eva:checkmark-fill"
                                        : "eva:copy-fill"
                                    }
                                    width={14}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {f.matchdayName ??
                            f.matchdayNumber ??
                            f.gameweek ??
                            "-"}
                        </TableCell>
                        <TableCell>
                          {f.kickoffAt
                            ? new Date(f.kickoffAt).toLocaleString()
                            : "-"}
                        </TableCell>
                        <TableCell>{getContestantName(f, "home")}</TableCell>
                        <TableCell>{getContestantName(f, "away")}</TableCell>
                        <TableCell>
                          <Chip
                            label={f.status ?? "-"}
                            size="small"
                            color={STATUS_COLORS[f.status] ?? "default"}
                          />
                        </TableCell>
                        <TableCell>
                          {f.homeScore != null && f.awayScore != null
                            ? `${f.homeScore} - ${f.awayScore}`
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      </Container>
    </>
  );
}
